import TPEAction from 'src/models/common/TPEAction';
import { AIInsightsState, INDEXING_PREFIX, Message } from 'src/services/ai-insights/AIInsightsState';

/**
 * Each action performs one complete state transition.
 *
 * They are deliberately coarse: TPEAction.withPayload() mutates a shared singleton and
 * returns it, so dispatching the same action twice before the reducer runs would make both
 * invocations read the final payload. Keeping transitions atomic means no caller ever needs
 * to dispatch the same action twice in a tick.
 */
export const ACTIONS = Object.freeze({
    /** Opens a turn: optionally shows the question, clears the error, adds the streaming target. */
    BEGIN_REQUEST: new TPEAction('BEGIN_REQUEST'),
    /** Appends a complete message to the transcript. */
    APPEND_MESSAGE: new TPEAction('APPEND_MESSAGE'),
    /** Shows the transient "indexing" notice in the streaming target. */
    SET_INDEXING_NOTICE: new TPEAction('SET_INDEXING_NOTICE'),
    /** Writes the accumulated streamed text, replacing any indexing notice. */
    SET_STREAMING_TEXT: new TPEAction('SET_STREAMING_TEXT'),
    /** Ends a turn with an error, discarding a streaming target that never received content. */
    FAIL_REQUEST: new TPEAction('FAIL_REQUEST'),
    /** Ends a turn successfully, discarding a streaming target that never received content. */
    COMPLETE_REQUEST: new TPEAction('COMPLETE_REQUEST'),
    SET_LOADING: new TPEAction('SET_LOADING'),
    START_POLLING: new TPEAction('START_POLLING'),
    SET_POLLING_ELAPSED: new TPEAction('SET_POLLING_ELAPSED'),
    STOP_POLLING: new TPEAction('STOP_POLLING'),
    /** Clears the transcript and every in-flight indicator, ready for a fresh conversation. */
    RESET_CHAT: new TPEAction('RESET_CHAT'),
});

/** True when the trailing message is an assistant entry, i.e. the streaming target. */
const hasStreamingTarget = (state: AIInsightsState): boolean => {
    const last = state.messages[state.messages.length - 1];
    return last != null && last.role === 'assistant';
};

/**
 * True when the streaming target holds no agent content — either untouched or showing only
 * the transient indexing notice. Such a target is discarded when a turn ends, so a turn that
 * produced nothing does not render as a permanently blank reply.
 */
const hasEmptyStreamingTarget = (state: AIInsightsState): boolean => {
    if (!hasStreamingTarget(state)) {
        return false;
    }
    const target = state.messages[state.messages.length - 1];
    // Match the full marker (emoji + space) rather than a trimmed prefix, so an agent reply
    // that happens to open with the emoji is never mistaken for the notice and discarded.
    return !target.text || target.text.startsWith(INDEXING_PREFIX);
};

/** Removes the trailing message. */
const dropStreamingTarget = (state: AIInsightsState): AIInsightsState =>
    ({ ...state, messages: state.messages.slice(0, state.messages.length - 1) });

/** Replaces the trailing message's text, leaving the rest of the transcript alone. */
const withStreamingText = (state: AIInsightsState, text: string): AIInsightsState => {
    const lastIdx = state.messages.length - 1;
    const messages = [...state.messages];
    messages[lastIdx] = { ...messages[lastIdx], text };
    return { ...state, messages };
};

export function aiInsightsReducer(state: AIInsightsState, action: TPEAction): AIInsightsState {
    switch (action.type) {
        case ACTIONS.BEGIN_REQUEST.type: {
            const { question, hideUserMessage } = action.payload as {
                question: string;
                hideUserMessage?: boolean;
            };
            const messages: Message[] = [...state.messages];
            if (!hideUserMessage) {
                messages.push({ role: 'user', text: question });
            }
            // The empty assistant entry is the target every streamed chunk writes into.
            messages.push({ role: 'assistant', text: '' });
            return { ...state, messages, loading: true, error: '' };
        }

        case ACTIONS.APPEND_MESSAGE.type: {
            return { ...state, messages: [...state.messages, action.payload as Message] };
        }

        case ACTIONS.SET_INDEXING_NOTICE.type: {
            if (!hasStreamingTarget(state)) {
                return state;
            }
            return withStreamingText(state, `${INDEXING_PREFIX}${action.payload as string}`);
        }

        case ACTIONS.SET_STREAMING_TEXT.type: {
            if (!hasStreamingTarget(state)) {
                return state;
            }
            // Overwrites the transient indexing notice as a side effect, which is intended:
            // once real content streams in, the notice is no longer relevant.
            return withStreamingText(state, action.payload as string);
        }

        case ACTIONS.FAIL_REQUEST.type: {
            const next = { ...state, error: action.payload as string, loading: false };
            // Drop the streaming target when it holds no content, so the error is not rendered
            // beside a blank or stale reply.
            return hasEmptyStreamingTarget(next) ? dropStreamingTarget(next) : next;
        }

        case ACTIONS.COMPLETE_REQUEST.type: {
            // The success counterpart of FAIL_REQUEST. An agent turn can complete having
            // streamed nothing — an empty reply, or a turn whose only outcome was triggering a
            // report — and the blank target would otherwise stay on screen forever.
            //
            // The payload is the full text streamed this turn. The terminal frame carries no
            // text of its own, so the reply is finalised from what was received: a reply that
            // streamed content must never end blank or be removed by completion.
            const finalText = typeof action.payload === 'string' ? action.payload : '';
            let next: AIInsightsState = { ...state, loading: false };
            if (finalText && hasStreamingTarget(next)) {
                next = withStreamingText(next, finalText);
            }
            return hasEmptyStreamingTarget(next) ? dropStreamingTarget(next) : next;
        }

        case ACTIONS.SET_LOADING.type: {
            return { ...state, loading: action.payload as boolean };
        }

        case ACTIONS.START_POLLING.type: {
            return {
                ...state,
                pollingReportId: action.payload as string,
                pollingStartedAt: Date.now(),
                pollingElapsed: 0,
            };
        }

        case ACTIONS.SET_POLLING_ELAPSED.type: {
            return { ...state, pollingElapsed: action.payload as number };
        }

        case ACTIONS.STOP_POLLING.type: {
            return { ...state, pollingReportId: undefined, pollingStartedAt: undefined };
        }

        case ACTIONS.RESET_CHAT.type: {
            return {
                ...state,
                messages: [],
                loading: false,
                error: '',
                pollingReportId: undefined,
                pollingStartedAt: undefined,
                pollingElapsed: 0,
            };
        }

        default:
            return state;
    }
}
